/**
 * Replace Product — single commercial action that preserves hierarchy.
 * Internally: remove old line + insert catalogue product; reparent kept Supporting Components.
 */

import type { DtoolsHierarchyOverrides } from "./dtoolsHierarchyOverrides";
import { applyHierarchyOverridesToLines } from "./dtoolsHierarchyOverrides";
import { isClarityStandardServiceProduct } from "./clarityServiceCatalogueItems";
import {
  appendReviseLine,
  createReviseProductLine,
  displayTitleForDtoolsReviseLine,
  type DtoolsReviseLine,
  type DtoolsReviseProductPick,
  type DtoolsReviseRoomRef,
} from "./dtoolsProposalRevise";
import { stableLineKeyFromDtoolsReviseLine } from "./variationOrderDtoolsMap";

export type ProductReplaceSnapshot = {
  lineKey: string;
  lineId: string;
  stableLineKey: string;
  brand: string | null;
  model: string | null;
  displayName: string;
  imageUrl: string | null;
  unitPrice: number;
  quantity: number;
  isAccessory: boolean;
};

export type SupportingComponentForReplace = {
  lineKey: string;
  lineId: string;
  stableLineKey: string;
  title: string;
  brand: string | null;
  model: string | null;
  depth: number;
  /** Direct parent line id within the tree (source primary or another accessory). */
  parentLineId: string;
};

export type ProductReplacementRecord = {
  /** Shared id linking the remove+add pair as one logical replacement. */
  replacementId: string;
  old: ProductReplaceSnapshot;
  next: ProductReplaceSnapshot;
  reason: string | null;
  retainedAccessoryKeys: string[];
  removedAccessoryKeys: string[];
  retainedAccessoryStableKeys: string[];
  removedAccessoryStableKeys: string[];
};

/** Stable id for one logical Replace Product action. */
export function createReplacementId(): string {
  return `repl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function lineIdsMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  return String(a ?? "").trim() !== "" && String(a).trim() === String(b ?? "").trim();
}

function snapshotOf(line: DtoolsReviseLine): ProductReplaceSnapshot {
  return {
    lineKey: line.key,
    lineId: line.id,
    stableLineKey: stableLineKeyFromDtoolsReviseLine(line),
    brand: line.manufacturer ?? null,
    model: line.model ?? null,
    displayName: displayTitleForDtoolsReviseLine(line),
    imageUrl: line.imageUrl ?? null,
    unitPrice: line.unitPrice,
    quantity: line.quantity,
    isAccessory: Boolean(line.parentId || line.isAccessory),
  };
}

function roomRefForLine(line: DtoolsReviseLine): DtoolsReviseRoomRef {
  return {
    systemName: String(line.systemName ?? "").trim() || "System",
    systemId: line.systemId ?? null,
    roomName: String(line.roomName ?? "").trim() || "Room",
    locationId: line.locationId ?? null,
  };
}

/** Direct + nested Supporting Components under a primary (depth-first). Empty when source is itself supporting. */
export function listSupportingComponentsForReplace(
  lines: DtoolsReviseLine[],
  sourceKey: string,
): SupportingComponentForReplace[] {
  const source = lines.find((l) => l.key === sourceKey);
  if (!source) return [];
  if (source.parentId || source.isAccessory) return [];

  const byParent = new Map<string, DtoolsReviseLine[]>();
  for (const line of lines) {
    const pid = String(line.parentId ?? "").trim();
    if (!pid) continue;
    const list = byParent.get(pid) ?? [];
    list.push(line);
    byParent.set(pid, list);
  }

  const out: SupportingComponentForReplace[] = [];
  const walk = (parentId: string, depth: number) => {
    const children = byParent.get(parentId) ?? [];
    for (const child of children) {
      out.push({
        lineKey: child.key,
        lineId: child.id,
        stableLineKey: stableLineKeyFromDtoolsReviseLine(child),
        title: displayTitleForDtoolsReviseLine(child),
        brand: child.manufacturer ?? null,
        model: child.model ?? null,
        depth,
        parentLineId: parentId,
      });
      walk(child.id, depth + 1);
    }
  };
  walk(source.id, 0);
  return out;
}

export function canReplaceDtoolsReviseLine(
  lines: DtoolsReviseLine[],
  sourceKey: string,
): { ok: true } | { ok: false; message: string } {
  const source = lines.find((l) => l.key === sourceKey);
  if (!source) {
    return { ok: false, message: "This product is no longer in the workspace and cannot be replaced." };
  }
  const ann = source.variationOrderAnnotation;
  if (ann?.kind === "removed") {
    return {
      ok: false,
      message: "Removed products cannot be replaced. Restore or add a new product instead.",
    };
  }
  return { ok: true };
}

/**
 * Replace a product while preserving System / Room / parent / kept Supporting Components.
 * Unticked accessories (and their nested children) are removed from the workspace.
 */
export function replaceDtoolsReviseProduct(input: {
  lines: DtoolsReviseLine[];
  sourceKey: string;
  product: DtoolsReviseProductPick;
  /** Keys of supporting components to keep. Default: keep all. Ignored when replacing a supporting item. */
  keepAccessoryKeys?: ReadonlySet<string> | readonly string[] | null;
  reason?: string | null;
  hierarchyOverrides?: DtoolsHierarchyOverrides | null;
}): {
  lines: DtoolsReviseLine[];
  hierarchyOverrides: DtoolsHierarchyOverrides;
  replacement: ProductReplacementRecord;
} {
  const gate = canReplaceDtoolsReviseLine(input.lines, input.sourceKey);
  if (!gate.ok) {
    throw new Error(gate.message);
  }

  const source = input.lines.find((l) => l.key === input.sourceKey)!;
  const applied = applyHierarchyOverridesToLines(
    input.lines,
    input.hierarchyOverrides ?? { parentByLineId: {} },
  ) as DtoolsReviseLine[];
  const sourceApplied = applied.find((l) => l.key === input.sourceKey) ?? source;
  const isSupporting = Boolean(sourceApplied.parentId || sourceApplied.isAccessory);

  const supporting = listSupportingComponentsForReplace(applied, input.sourceKey);
  const keepSet = isSupporting
    ? new Set<string>()
    : input.keepAccessoryKeys == null
      ? new Set(supporting.map((s) => s.lineKey))
      : new Set(
          Array.from(input.keepAccessoryKeys as Iterable<string>).map((k) => String(k)),
        );

  // If a parent accessory is removed, force-remove its descendants too.
  const removedKeys = new Set<string>();
  for (const row of supporting) {
    if (!keepSet.has(row.lineKey)) {
      removedKeys.add(row.lineKey);
    }
  }
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of supporting) {
      if (removedKeys.has(row.lineKey)) continue;
      const parentAcc = supporting.find((s) => s.lineId === row.parentLineId);
      if (parentAcc && removedKeys.has(parentAcc.lineKey)) {
        removedKeys.add(row.lineKey);
        changed = true;
      }
    }
  }

  const retained = supporting.filter((s) => !removedKeys.has(s.lineKey));
  const removed = supporting.filter((s) => removedKeys.has(s.lineKey));

  const withoutSourceAndRemoved = applied.filter(
    (l) => l.key !== source.key && !removedKeys.has(l.key),
  );

  const room = roomRefForLine(sourceApplied);
  let replacementLine = createReviseProductLine(room, withoutSourceAndRemoved, input.product);
  const isClarityService = isClarityStandardServiceProduct({
    brand: input.product.brand,
    manufacturer: input.product.brand,
    model: input.product.model,
    name: input.product.displayName,
    displayName: input.product.displayName,
    sku: input.product.sku,
    catalogueKey: input.product.catalogueKey,
  });
  const nextQty = isClarityService ? 1 : Math.max(1, sourceApplied.quantity);
  replacementLine = {
    ...replacementLine,
    quantity: nextQty,
    lineTotal:
      String(input.product.pricingMethod ?? "").toLowerCase() === "percentage_of_equipment"
        ? Math.max(0, replacementLine.lineTotal)
        : Math.max(0, input.product.unitPrice) * nextQty,
    sortOrder: sourceApplied.sortOrder,
    parentId: isSupporting ? sourceApplied.parentId : null,
    isAccessory: isSupporting,
  };

  // Insert replacement in the same array position as the source when possible.
  const insertAt = Math.max(
    0,
    applied.findIndex((l) => l.key === source.key),
  );
  const withReplacement = [
    ...withoutSourceAndRemoved.slice(0, insertAt),
    replacementLine,
    ...withoutSourceAndRemoved.slice(insertAt),
  ];

  // Reparent kept direct children of the old primary onto the new primary.
  const remapped = withReplacement.map((line) => {
    if (!lineIdsMatch(line.parentId, sourceApplied.id)) return line;
    if (removedKeys.has(line.key)) return line;
    return { ...line, parentId: replacementLine.id, isAccessory: true };
  });

  const nextOverrides: DtoolsHierarchyOverrides = {
    parentByLineId: {
      ...(input.hierarchyOverrides?.parentByLineId ?? {}),
    },
  };
  // Drop overrides for removed / replaced source; set new primary/supporting parent.
  delete nextOverrides.parentByLineId[sourceApplied.id];
  for (const r of removed) {
    delete nextOverrides.parentByLineId[r.lineId];
  }
  if (isSupporting) {
    nextOverrides.parentByLineId[replacementLine.id] = sourceApplied.parentId;
  } else {
    nextOverrides.parentByLineId[replacementLine.id] = null;
    for (const line of remapped) {
      if (lineIdsMatch(line.parentId, replacementLine.id)) {
        nextOverrides.parentByLineId[line.id] = replacementLine.id;
      }
    }
  }

  const finalLines = applyHierarchyOverridesToLines(remapped, nextOverrides) as DtoolsReviseLine[];
  // Ensure replacement still present after override apply
  if (!finalLines.some((l) => l.key === replacementLine.key)) {
    throw new Error("Replace Product failed to insert the replacement line.");
  }

  const nextSnap = snapshotOf(finalLines.find((l) => l.key === replacementLine.key)!);
  const reason = String(input.reason ?? "").trim() || null;

  return {
    lines: finalLines,
    hierarchyOverrides: nextOverrides,
    replacement: {
      replacementId: createReplacementId(),
      old: snapshotOf(sourceApplied),
      next: nextSnap,
      reason,
      retainedAccessoryKeys: retained.map((r) => r.lineKey),
      removedAccessoryKeys: removed.map((r) => r.lineKey),
      retainedAccessoryStableKeys: retained.map((r) => r.stableLineKey),
      removedAccessoryStableKeys: removed.map((r) => r.stableLineKey),
    },
  };
}

/** Collapse A→B + B→C draft pairs into a single A→C logical replacement. */
export function normalizeReplacementChains<
  T extends {
    oldStableLineKey: string;
    newStableLineKey: string;
    reason?: string | null;
    removedAccessoryStableKeys?: string[];
    retainedAccessoryStableKeys?: string[];
    replacementId?: string;
    oldDisplayName?: string;
    newDisplayName?: string;
  },
>(replacements: T[]): T[] {
  if (replacements.length <= 1) return replacements;
  const byOld = new Map(replacements.map((r) => [r.oldStableLineKey, r]));
  const byNew = new Map(replacements.map((r) => [r.newStableLineKey, r]));
  const consumed = new Set<string>();
  const out: T[] = [];

  for (const r of replacements) {
    const key = `${r.oldStableLineKey}=>${r.newStableLineKey}`;
    if (consumed.has(key)) continue;

    // Walk forward from this pair while its new key is someone else's old key.
    let head = r;
    let tip = r;
    const chainKeys = [key];
    while (byOld.has(tip.newStableLineKey)) {
      const next = byOld.get(tip.newStableLineKey)!;
      const nextKey = `${next.oldStableLineKey}=>${next.newStableLineKey}`;
      if (chainKeys.includes(nextKey)) break;
      tip = next;
      chainKeys.push(nextKey);
    }
    // Walk backward so we always start from the original removed product.
    while (byNew.has(head.oldStableLineKey)) {
      const prev = byNew.get(head.oldStableLineKey)!;
      const prevKey = `${prev.oldStableLineKey}=>${prev.newStableLineKey}`;
      if (chainKeys.includes(prevKey)) break;
      head = prev;
      chainKeys.unshift(prevKey);
    }

    for (const k of chainKeys) consumed.add(k);

    if (head === tip) {
      out.push(head);
      continue;
    }

    const removedAccessoryStableKeys = [
      ...new Set([
        ...(head.removedAccessoryStableKeys ?? []),
        ...(tip.removedAccessoryStableKeys ?? []),
      ]),
    ];
    out.push({
      ...head,
      replacementId: tip.replacementId ?? head.replacementId,
      newStableLineKey: tip.newStableLineKey,
      newDisplayName: tip.newDisplayName ?? head.newDisplayName,
      reason: tip.reason ?? head.reason,
      retainedAccessoryStableKeys: tip.retainedAccessoryStableKeys ?? head.retainedAccessoryStableKeys,
      removedAccessoryStableKeys,
    });
  }

  return out;
}

/** Apply replacement pairing metadata onto VO deltas (still added/removed financially). */
export function applyReplacementPairingToVoDeltas<
  T extends {
    changeType: string;
    stableLineKey: string;
    changeReason?: string | null;
    replacesStableLineKey?: string | null;
    replacedByStableLineKey?: string | null;
    replacementId?: string | null;
  },
>(
  deltas: T[],
  replacements: Array<{
    replacementId?: string;
    oldStableLineKey: string;
    newStableLineKey: string;
    reason?: string | null;
    removedAccessoryStableKeys?: string[];
  }>,
): T[] {
  if (!replacements.length) return deltas;
  const normalized = normalizeReplacementChains(replacements);
  const byOld = new Map(normalized.map((r) => [r.oldStableLineKey, r]));
  const byNew = new Map(normalized.map((r) => [r.newStableLineKey, r]));
  const accessoryOwner = new Map<string, (typeof normalized)[number]>();
  for (const r of normalized) {
    for (const key of r.removedAccessoryStableKeys ?? []) {
      accessoryOwner.set(key, r);
    }
  }

  return deltas.map((d) => {
    if (d.changeType === "removed") {
      const pair = byOld.get(d.stableLineKey) ?? accessoryOwner.get(d.stableLineKey);
      if (!pair) return d;
      const shared = String(pair.reason ?? "").trim() || null;
      const isPrimaryOld = pair.oldStableLineKey === d.stableLineKey;
      return {
        ...d,
        replacementId: pair.replacementId ?? null,
        replacedByStableLineKey: isPrimaryOld ? pair.newStableLineKey : d.replacedByStableLineKey ?? null,
        // Primary removed line + unticked accessories inherit the single replacement reason.
        changeReason: shared ?? (String(d.changeReason ?? "").trim() || null),
      };
    }
    if (d.changeType === "added") {
      const pair = byNew.get(d.stableLineKey);
      if (!pair) return d;
      const shared = String(pair.reason ?? "").trim() || null;
      return {
        ...d,
        replacementId: pair.replacementId ?? null,
        replacesStableLineKey: pair.oldStableLineKey,
        changeReason: shared ?? (String(d.changeReason ?? "").trim() || null),
      };
    }
    return d;
  });
}

/**
 * Write one shared reason onto both primary keys and any unticked accessory removals.
 */
export function syncSharedReplacementReason(
  changeReasons: Record<string, string>,
  replacement: {
    oldStableLineKey: string;
    newStableLineKey: string;
    removedAccessoryStableKeys?: string[];
  },
  reason: string | null,
): Record<string, string> {
  const next = { ...changeReasons };
  const trimmed = String(reason ?? "").trim();
  const keys = [
    replacement.oldStableLineKey,
    replacement.newStableLineKey,
    ...(replacement.removedAccessoryStableKeys ?? []),
  ];
  for (const key of keys) {
    if (!key) continue;
    if (trimmed) next[key] = trimmed;
    else delete next[key];
  }
  return next;
}

/** Stable keys that belong to a logical replacement pair (primary old/new only). */
export function pairedReplacementPrimaryKeys(
  replacements: Array<{ oldStableLineKey: string; newStableLineKey: string }>,
): Set<string> {
  const keys = new Set<string>();
  for (const r of replacements) {
    keys.add(r.oldStableLineKey);
    keys.add(r.newStableLineKey);
  }
  return keys;
}

export type VoReplacementPair = {
  removed: import("./variationOrderDiff").VoDeltaLine;
  added: import("./variationOrderDiff").VoDeltaLine;
  reason: string | null;
  commercialDelta: number;
  replacementId: string | null;
};

/** Group paired remove+add deltas for customer-facing VO document. */
export function groupVoReplacementPairs(
  deltas: import("./variationOrderDiff").VoDeltaLine[],
): {
  replacements: VoReplacementPair[];
  unpaired: import("./variationOrderDiff").VoDeltaLine[];
} {
  const removed = deltas.filter((d) => d.changeType === "removed");
  const added = deltas.filter((d) => d.changeType === "added");
  const other = deltas.filter((d) => d.changeType !== "removed" && d.changeType !== "added");
  const usedRemoved = new Set<string>();
  const usedAdded = new Set<string>();
  const replacements: VoReplacementPair[] = [];

  for (const a of added) {
    const oldKey = a.replacesStableLineKey;
    if (!oldKey) continue;
    const r = removed.find((x) => x.stableLineKey === oldKey);
    if (!r) continue;
    usedRemoved.add(r.stableLineKey);
    usedAdded.add(a.stableLineKey);
    const reason = a.changeReason?.trim() || r.changeReason?.trim() || null;
    replacements.push({
      removed: r,
      added: a,
      reason,
      commercialDelta: (a.lineTotalDelta ?? 0) + (r.lineTotalDelta ?? 0),
      replacementId: a.replacementId ?? r.replacementId ?? null,
    });
  }
  for (const r of removed) {
    if (usedRemoved.has(r.stableLineKey)) continue;
    const newKey = r.replacedByStableLineKey;
    if (!newKey) continue;
    const a = added.find((x) => x.stableLineKey === newKey);
    if (!a || usedAdded.has(a.stableLineKey)) continue;
    usedRemoved.add(r.stableLineKey);
    usedAdded.add(a.stableLineKey);
    const reason = a.changeReason?.trim() || r.changeReason?.trim() || null;
    replacements.push({
      removed: r,
      added: a,
      reason,
      commercialDelta: (a.lineTotalDelta ?? 0) + (r.lineTotalDelta ?? 0),
      replacementId: a.replacementId ?? r.replacementId ?? null,
    });
  }

  const unpaired = [
    ...removed.filter((d) => !usedRemoved.has(d.stableLineKey)),
    ...added.filter((d) => !usedAdded.has(d.stableLineKey)),
    ...other,
  ];
  return { replacements, unpaired };
}
